import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SEO from "@/components/SEO";
import { api, LINKEDIN_URL } from "@/lib/api";
import { toast } from "sonner";
import { Linkedin, Mail, Loader2, Send } from "lucide-react";

export default function Contact() {
    const [params] = useSearchParams();
    const [form, setForm] = useState({ name: "", email: "", topic: "", message: "" });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const topic = params.get("topic");
        if (topic) {
            setForm((f) => ({
                ...f,
                topic,
                message: topic === "Get Featured"
                    ? "Hi Chandrashekar, I'd love my venture to be listed in the Purposeful Directory. Here's a bit about us: "
                    : f.message,
            }));
        }
    }, [params]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/contact", form);
            toast.success("Thanks. I'll get back to you soon.");
            setForm({ name: "", email: "", topic: "", message: "" });
        } catch (err) {
            const m = err?.response?.data?.detail;
            toast.error(typeof m === "string" ? m : "Could not send your message.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <main data-testid="contact-page" className="text-white pt-32 pb-24">
            <SEO
                title="Contact | Chandrashekar Bhat | Purposeful, Inc."
                description="Get in touch about the book Purposeful, Inc., The Purposeful Network, founder interviews or speaking. Email purposeful.inc@gmail.com or connect on LinkedIn."
                path="/contact"
                keywords="contact Purposeful Inc, Chandrashekar Bhat contact, book inquiry, founder interview request, speaking inquiry"
                schema={{
                    "@context": "https://schema.org",
                    "@type": "ContactPage",
                    "url": "https://purposeful.inc/contact",
                    "name": "Contact Purposeful, Inc.",
                    "isPartOf": { "@id": "https://purposeful.inc/#website" },
                    "mainEntity": {
                        "@type": "Organization",
                        "@id": "https://purposeful.inc/#organization",
                        "email": "purposeful.inc@gmail.com",
                        "sameAs": ["https://www.linkedin.com/in/buildwithbhat/"]
                    }
                }}
                breadcrumbs={[{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]}
            />
            <section className="container-x mx-auto max-w-5xl grid md:grid-cols-2 gap-16">
                <div>
                    <div className="overline mb-4">Get in touch</div>
                    <h1 className="font-display text-5xl md:text-6xl font-semibold tracking-tight">
                        Say <span className="text-[#D4AF37]">hello.</span>
                    </h1>
                    <p className="mt-5 text-zinc-400 leading-relaxed">
                        For book inquiries, founder interviews, speaking, or
                        anything to do with The Purposeful Network, write below
                        or find me on LinkedIn.
                    </p>
                    <div className="mt-8 space-y-3 text-sm">
                        <a href={LINKEDIN_URL} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-zinc-300 hover:text-[#D4AF37]" data-testid="contact-linkedin-link">
                            <Linkedin size={16} /> linkedin.com/in/buildwithbhat
                        </a>
                        <a href="mailto:purposeful.inc@gmail.com" className="flex items-center gap-3 text-zinc-300 hover:text-[#D4AF37]" data-testid="contact-email-link">
                            <Mail size={16} /> purposeful.inc@gmail.com
                        </a>
                    </div>
                </div>

                <form onSubmit={submit} data-testid="contact-form" className="space-y-4">
                    <input
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Your name"
                        data-testid="contact-name-input"
                        className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37] transition"
                    />
                    <input
                        required
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="Email"
                        data-testid="contact-email-input"
                        className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37] transition"
                    />
                    <input
                        value={form.topic}
                        onChange={(e) => setForm({ ...form, topic: e.target.value })}
                        placeholder="Topic (Book / Network / Interview / Other)"
                        data-testid="contact-topic-input"
                        className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37] transition"
                    />
                    <textarea
                        required
                        rows={6}
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        placeholder="What's on your mind?"
                        data-testid="contact-message-input"
                        className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37] transition resize-none"
                    />
                    <button type="submit" disabled={loading} data-testid="contact-submit-button" className="btn-gold w-full sm:w-auto disabled:opacity-60">
                        {loading ? <Loader2 size={16} className="animate-spin" /> : <>Send message <Send size={14} /></>}
                    </button>
                </form>
            </section>
        </main>
    );
}
